import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import { Card, Stack, Button, Container, Typography, Box, Grid, TextField, Tab, IconButton } from '@mui/material';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';

// components
import { toast } from 'react-toastify';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';

// api
import { createNotification, deleteNotification, getNotification, sendPushNotification } from '../../api/users.api';

// ----------------------------------------------------------------------

export default function User() {
  // Common state for both tabs
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [loading, setLoading] = useState(false);

  // State for Local Notifications
  const [localNotifications, setLocalNotifications] = useState([]);

  // State for Push Notifications
  const [targetUsername, setTargetUsername] = useState('');

  // State for Tabs
  const [tabValue, setTabValue] = useState('1');

  useEffect(() => {
    handleGetLocalNotifications();
  }, []);

  const handleTabChange = (event, newValue) => {
    // Clear form when switching tabs for a better user experience
    setTitle('');
    setSubtitle('');
    setTargetUsername('');
    setTabValue(newValue);
  };

  // --- Local Notification Logic ---
  const handleGetLocalNotifications = async () => {
    const response = await getNotification();
    if (response.ok) {
      setLocalNotifications(response.data.data);
    }
  };

  const handleRemoveLocalNotification = async (notifyId) => {
    await deleteNotification(notifyId);
    setLocalNotifications(localNotifications.filter((item) => item.id !== notifyId));
  };

  const handleAddLocalNotification = async () => {
    if (title.length < 1 || subtitle.length < 1) {
      return; // Consider adding user feedback (e.g., snackbar)
    }
    setLoading(true);
    const payload = {
      title,
      subtitle,
      priority: 'medium',
      type: 'Announcement',
    };
    const response = await createNotification(payload);
    if (response.ok) {
      setLocalNotifications([...localNotifications, response.data.data]);
      setTitle('');
      setSubtitle('');
    }
    setLoading(false);
  };

  // --- Push Notification Logic ---
  const handleSendPushNotification = async () => {
    if (title.length < 1 || subtitle.length < 1) {
      // You can add better user feedback here, e.g., a snackbar
      return;
    }

    setLoading(true);

    // If targetUsername is empty, send to all. Otherwise, send to the specified user.
    const payload = {
      title,
      body: subtitle,
      username: targetUsername,
    };

    // This is a mock API call. Replace with your actual implementation.
    const result = await sendPushNotification(payload);

    if (result.ok) {
      // Add success feedback for the admin (e.g., a success snackbar)
      toast.success('Push notification sent successfully with payload:', payload);
      setTitle('');
      setSubtitle('');
      setTargetUsername('');
    } else {
      // Add error handling feedback (e.g., an error snackbar)
      toast.error('Failed to send push notification');
    }

    setLoading(false);
  };

  return (
    <Page title="Notifications">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Send Notifications
          </Typography>
          {/* This button might not be relevant anymore, but leaving it as per original code */}
          <Button variant="contained" component={RouterLink} to="#" startIcon={<Iconify icon="eva:plus-fill" />}>
            New User
          </Button>
        </Stack>

        <Card>
          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleTabChange} aria-label="notification tabs">
                <Tab label="Local Notification" value="1" />
                <Tab label="Push Notification" value="2" />
              </TabList>
            </Box>

            {/* Local Notification Tab Panel */}
            <TabPanel value="1" sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {localNotifications.map((notification) => (
                  <Grid key={notification.id} item xs={12} sm={6} md={4}>
                    <Card sx={{ p: 2, boxShadow: 3 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {notification.title}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveLocalNotification(notification.id)}
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      </Stack>
                      <Typography variant="body2">{notification.subtitle}</Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Stack marginTop={4} direction="column" spacing={2}>
                <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                <TextField
                  fullWidth
                  label="Notification Body"
                  multiline
                  rows={4}
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                />
                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={loading}
                  onClick={handleAddLocalNotification}
                >
                  Add Local Notification
                </LoadingButton>
              </Stack>
            </TabPanel>

            {/* Push Notification Tab Panel */}
            <TabPanel value="2" sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Typography variant="h6">Compose Push Notification</Typography>
                <TextField
                  fullWidth
                  label="Target Username (optional)"
                  value={targetUsername}
                  onChange={(e) => setTargetUsername(e.target.value)}
                  helperText="Leave blank to send to all users"
                />
                <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                <TextField
                  fullWidth
                  label="Message Body"
                  multiline
                  rows={4}
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                />
                <LoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  loading={loading}
                  onClick={handleSendPushNotification}
                  disabled={!title || !subtitle}
                >
                  {`Send to ${targetUsername || 'All Users'}`}
                </LoadingButton>
              </Stack>
            </TabPanel>
          </TabContext>
        </Card>
      </Container>
    </Page>
  );
}
